import type { ContentItem } from "@edco/apollo";
import type { MaybeRefOrGetter } from "vue";
import type { Script } from "@unhead/vue";
import type { ISeoComposition } from "~/models/generated/ISeoComposition";
import type { ISearchResultComposition } from "~/models/generated/ISearchResultComposition";

type MigratedSeoContent = ContentItem<Partial<ISeoComposition & ISearchResultComposition>>;

/**
 * Setup standard SEO meta tags for a migrated content item.
 */
export function useMigratedSeo ( content: MaybeRefOrGetter<MigratedSeoContent | null | undefined> ) {
  useHead( computed( () => {
    const contentItem = toValue( content );
    if ( !contentItem ) return {};

    const properties = contentItem.properties;
    const title = properties.seoPageTitle || contentItem.name;
    const description = properties.seoMetaDescription || properties.searchDescription || "";
    const socialImageUrl = properties.seoSocialImage?.[0]?.url || properties.searchThumbnail?.[0]?.url || "";
    const url = contentItem.route?.path ?? "";
    const robotsValue = [
      properties.seoDoNotIndex ? "noindex" : "index",
      properties.seoDoNotFollow ? "nofollow" : "follow",
    ].join( ", " );

    const scripts: Script[] = [];
    if ( properties.seoJsonLdScript ) {
      try {
        scripts.push( { type: "application/ld+json", json: JSON.parse( properties.seoJsonLdScript ) } );
      } catch {
        // Invalid JSON-LD — skip silently
      }
    }

    return {
      title,
      link: properties.seoCanonicalUrl ? [{ rel: "canonical", href: properties.seoCanonicalUrl }] : [],
      script: scripts,
      meta: [
        { name: "description", content: description },
        { name: "keywords", content: properties.seoKeyWords ?? "" },
        ...( robotsValue ? [{ name: "robots", content: robotsValue }] : [] ),
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: socialImageUrl },
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_GB" },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:url", content: url },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: socialImageUrl },
      ],
    };
  } ) );
}
